/**
 * Peer-navigation receiver — Einstein side (Galileo -> Einstein).
 *
 * The mirror of Galileo's `usePeerNavigationReceiver`. Galileo forwards a
 * navigation intent to the Einstein API, which pushes a `PeerNavigateIntent` to
 * every Einstein connection of the user over SignalR. Exactly one user-chosen tab
 * acts on it — the designated recipient, which in Einstein is the installed PWA
 * (see `galileoRecipientTab`). The acting tab navigates in place, focuses itself,
 * and acknowledges via the hub so the originating Galileo tab suppresses its
 * `window.open` fallback (no duplicate tabs). Intents whose path is not a known
 * Einstein route are dropped at the boundary.
 *
 * Mount once, as a sibling of <Routes> (so it has router context and survives
 * route transitions) — see `PeerNavigationReceiverBridge`.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useRealtimeConnection } from "../realtime/useConnectionState";
import {
    PeerNavigateIntentSchema,
    RealtimeEvents,
    RealtimeHubMethods,
} from "../realtime/notifications";

import { isEinsteinRoutePath } from "./einsteinRoutes";
import {
    autoDesignateInstalledPwa,
    isDesignatedTab,
    reclaimStaleDesignation,
} from "./galileoRecipientTab";

// ---------------------------------------------------------------------------
// Pure decision: gate -> parse -> validate -> act. Effects are injected so the
// rule can be tested without a SignalR connection, a router, or a real tab.
// ---------------------------------------------------------------------------

export interface PeerNavigationEffects {
    /** Whether this tab is the recipient (the installed PWA). */
    readonly isDesignated: () => boolean;
    /** Navigate the app to a validated, known route path. */
    readonly navigate: (path: string) => void;
    /** Bring this window forward (best-effort). */
    readonly focus: () => void;
    /** Acknowledge back to the originating Galileo tab. */
    readonly ack: (correlationId: string, replyConnectionId: string, source: string) => void;
}

/**
 * Decide and act on a single inbound intent. A non-designated tab does nothing;
 * a malformed or unknown-route intent is dropped at the boundary; a valid intent
 * on the designated tab navigates, focuses, and acks. No-throw by contract.
 */
export function handlePeerNavigateIntent(raw: unknown, effects: PeerNavigationEffects): void {
    // Gate first: a non-designated tab does nothing, not even parse-log noise.
    if (!effects.isDesignated()) return;

    const parsed = PeerNavigateIntentSchema.safeParse(raw);
    if (!parsed.success) {
        console.warn("[PeerNavigation] intent rejected at boundary", parsed.error);
        return;
    }
    const { path, correlationId, source, replyConnectionId } = parsed.data;

    // Validate the path is a known route before navigating — a malformed or
    // future-versioned path must not push the app into an unexpected state.
    if (!isEinsteinRoutePath(path)) {
        console.warn("[PeerNavigation] unknown path, ignoring:", path);
        return;
    }

    effects.navigate(path);
    // Best-effort: bring this window forward so the user sees the navigation.
    effects.focus();
    // Acknowledge so the originating Galileo tab cancels its window.open fallback.
    effects.ack(correlationId, replyConnectionId, source);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Subscribe to cross-app navigation intents pushed over SignalR. Only the
 * designated recipient tab (the installed PWA) navigates and acks; other tabs
 * receive the same push but ignore it.
 */
export function usePeerNavigationReceiver(): void {
    const connection = useRealtimeConnection();
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);

    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);

    useEffect(() => {
        // Housekeeping first: clear a slot stranded by a previously-closed
        // recipient. Then let the installed PWA claim itself as the recipient so a
        // click in Galileo lands here without any manual designation (a plain
        // browser tab is a no-op and never steals the slot).
        void reclaimStaleDesignation();
        autoDesignateInstalledPwa();
    }, []);

    useEffect(() => {
        if (connection === null) return;

        const handleIntent = (raw: unknown): void => {
            handlePeerNavigateIntent(raw, {
                isDesignated: isDesignatedTab,
                navigate: (path) => navigateRef.current(path),
                focus: () => window.focus(),
                ack: (correlationId, replyConnectionId, source) => {
                    connection
                        .invoke(
                            RealtimeHubMethods.ackPeerNavigation,
                            correlationId,
                            replyConnectionId,
                            source,
                        )
                        .catch(() => {
                            // Advisory: a failed ack only risks the sender opening one extra tab.
                        });
                },
            });
        };

        connection.on(RealtimeEvents.peerNavigateIntent, handleIntent);
        return () => {
            connection.off(RealtimeEvents.peerNavigateIntent, handleIntent);
        };
    }, [connection]);
}
